/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SearchFormProps } from './SearchForm.types';
import { StyledSearchForm, StyledSearchFormWrapper } from './SearchForm.styles';
import { useSearchFormStore } from '../../modules/store/searchFormStore';
import { getDateObj, getTotalNumberOfDays } from '@marriott/mi-ui-library';
import { SearchFormView } from './SearchForm.controller';
import clsx from 'clsx';
import { StaticDataContextProvider } from '../../modules/context';
import { DATES, HOME_PAGE_HEADER_CONST_HEIGHT, SERP_VALIDATE_URL } from '../../modules/constants';
import {
  DESTINATION,
  RESORTS_HIDDEN_FIELDS,
  ROOMS_AND_GUESTS,
  SPECIAL_RATES,
  USE_POINTS,
} from '../../modules/store/store.constants';
/* eslint-disable @nx/enforce-module-boundaries */
import { getSessionData } from '@marriott/mi-headless-utils';
import { useClientEnvVarsStore, useDCAPageModelStore, usePersistentGlobalStore } from '@marriott/mi-store-utils';
import { logger } from '@marriott/mi-headless-utils';
/* eslint-enable @nx/enforce-module-boundaries */
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { toRem } from '@marriott/mi-ui-library';
import {
  constructSearchformLabels,
  isConcurFlowActive,
  extractConcurCode,
  getSpecialRatesForSearch,
  hasConcurUserOverride,
  clearConcurUserOverride,
} from '../../modules/utils/searchFormHelper';
import { getDateFormattedFromURL } from '../../utils/src/DateUtils';
import { getRoomRange } from '../../utils/src/utils';
import React from 'react';
import { ADF_SEARCH_CTA_ENDPOINT_URL } from '../../constants/Calendar.constant';
import { enableConversationalSearch, IUpdatedPageModel } from '../../modules/utils/searchABTestUtil';
import { useGetUserInfo } from '../../hooks/lib/useGetUserInfo';
import { FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';

if (global && !global.loggerInstance) {
  global.loggerInstance = logger({ requestID: '', sessionID: '' }) as CallableFunction;
}

const SEARCHFORM_SESSION_FETCH_KEY = '__miShopSearchFormSessionFetched';
const SEARCHFORM_CONVO_AI_CACHE_KEY = '__miShopSearchFormConvoAICache';

type ConvoAISearchResult = ReturnType<typeof enableConversationalSearch>;

const defaultConvoAIResult: ConvoAISearchResult = {
  enableConvoAI: false,
  activityName: undefined,
  experienceName: undefined,
};

const getTargetConvoAIMbox = (mboxData?: IUpdatedPageModel['mboxData']) =>
  mboxData?.find(component => component?.mboxName === 'target-convoAI-mbox');

const readConvoAICache = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as Window & { [SEARCHFORM_CONVO_AI_CACHE_KEY]?: ConvoAISearchResult })[SEARCHFORM_CONVO_AI_CACHE_KEY];
};

const writeConvoAICache = (value: ConvoAISearchResult) => {
  if (typeof window === 'undefined') {
    return;
  }

  (window as Window & { [SEARCHFORM_CONVO_AI_CACHE_KEY]?: ConvoAISearchResult })[SEARCHFORM_CONVO_AI_CACHE_KEY] = value;
};

/**
 * Refer /libs/mi-shop-components/Readme.md for details of configuration variables for the Searchform
 * model ={<An AEM model with all author value>}
 * @param props
 * @returns
 */
// Use named rather than default exports.
export const SearchForm: FC<SearchFormProps> = React.memo(props => {
  const searchFormOnHomepage = props?.model?.['enableOverlayTop']; // this is the identifier which tells if the searchform is on HP or any other page
  const { model, isPositionedExternally, isSticky, refCallback, isNonStickyVariant } = props;
  const TabbedForm =
    typeof model?.['styleclass'] === 'string' && model?.['styleclass'].toLowerCase() === 'phoenix' ? true : false; // this tells us if the searchform is Home Page Search form or Advanced Search form

  /**
   * constructSearchformLabels function maps few labels to work with common react code ,
   * so we do not have to maintain 2 different labels for single component even if AEM
   * is sending two different label names for Advanced search form and Home page search form.
   */
  constructSearchformLabels(model);
  const callGetSessionData =
    props?.model?.numberOfSearchForms ||
    props?.model?.enableOverlayTop ||
    props?.model?.searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL ||
    props?.model?.searchCTAEndpointURL === SERP_VALIDATE_URL
      ? true
      : false; // flag to determine if get session call is required.
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData); // function to update global data store
  const globalData = usePersistentGlobalStore((state: any) => state.globalData); // global data extracted from global data store
  /**
   * shop store to access the data
   */
  const shopStoreInitialization = useSearchFormStore((state: any) => state.shopStoreInitialization);
  const setSearchFormState = useSearchFormStore((state: any) => state.setSearchFormState); // function to update search form data in store
  const isTabbedSearchContainer = useSearchFormStore((state: any) => state?.tabbedSearchFormContainer); // extracting tabbedSearchFormContainer from search form store
  const isShopStoreInitializedFromStore = useSearchFormStore((state: any) => state?.isShopStoreInitialized); // flag to check if shop store is initialized from TabbedSearchForm
  const isBaseLineSearchTabbedView = TabbedForm && !isTabbedSearchContainer?.tabbedSearchFormContainer; // variable to determine whether tabbed view is in Base line search form or from the TabbedSearchForm
  const [currentLocale, setCurrentLocale] = useState(props.currentLocale);
  const [isClientSideRenderedSearchform, setIsClientSideRenderedSearchform] = useState(false);
  const { envVarsObject } = useClientEnvVarsStore();
  const userDetailsState = useUserDetailsGlobalStore();
  const concurCodeApplied = useRef<boolean>(false);
  const [isStoreInitialized, setIsStoreInitialized] = useState(false);
  const isMBOP = props?.isMBOP ?? false;

  /**
   * DCA (Digital Content Assembly) mbox data from Adobe Target.
   * Subscribing only to mboxData avoids rerenders from unrelated pageModel changes.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dcaMboxData = useDCAPageModelStore((state: any) => state.pageModel?.mboxData) as
    | IUpdatedPageModel['mboxData']
    | undefined;

  /**
   * Evaluate conversational AI experiment assignment directly from DCA data.
   * Use memoized derived values instead of local state updates to avoid render flicker.
   */
  const convoAICache = readConvoAICache();
  const [convoAIResult, setConvoAIResult] = useState<ConvoAISearchResult>(convoAICache ?? defaultConvoAIResult);
  const isConvoAIProcessedRef = useRef(!!convoAICache);

  useEffect(() => {
    const isConvoAIEligible = !model.isTabbedSearchForm && model.enableConvoAISearch;

    if (!isConvoAIEligible) {
      setConvoAIResult(defaultConvoAIResult);
      return;
    }

    if (isConvoAIProcessedRef.current) {
      return;
    }

    const targetConvoAIMbox = getTargetConvoAIMbox(dcaMboxData);
    if (!targetConvoAIMbox?.uxlResponse) {
      return;
    }

    const evaluatedConvoAIResult = enableConversationalSearch({ mboxData: dcaMboxData } as IUpdatedPageModel);
    isConvoAIProcessedRef.current = true;
    setConvoAIResult(evaluatedConvoAIResult);
    writeConvoAICache(evaluatedConvoAIResult);
  }, [model.isTabbedSearchForm, model.enableConvoAISearch, dcaMboxData]);

  const { enableConvoAI: enableConversationalAISearch, activityName, experienceName } = convoAIResult;

  /**
   * import search from component with modification
   * and state implemention
   */

  useEffect(() => {
    setSearchFormState(
      [RESORTS_HIDDEN_FIELDS],
      {
        [RESORTS_HIDDEN_FIELDS]: {
          hotelType: model?.hotelType,
          activities: model?.resortActivities,
          amenities: model?.amenities,
        },
      },
      true
    );
  }, [model?.hotelType, model?.resortActivities, model?.amenities, setSearchFormState]);

  useEffect(() => {
    // Initialize the zustand store with the sessionData available in props
    if (!callGetSessionData && !model.isTabbedSearchForm) {
      shopStoreInitialization(globalData?.sessionData, model);
      setIsStoreInitialized(true);

      // Re-apply Concur special rates if they were set before store reinitialization
      const featureFlagsLocal = useClientEnvVarsStore.getState().envVarsObject['featureFlags'] as unknown as
        | Record<string, any>
        | undefined;
      const sessionDoc = globalData?.sessionData?.data || globalData?.sessionData?.cacheData?.data;
      if (isConcurFlowActive(featureFlagsLocal, sessionDoc) && concurCodeApplied.current && !hasConcurUserOverride()) {
        const concurCode = extractConcurCode(useUserDetailsGlobalStore.getState());
        if (concurCode) {
          const resolved = getSpecialRatesForSearch(null, null, concurCode, false, model?.specialRatesListItems || []);
          setSearchFormState([SPECIAL_RATES], {
            [SPECIAL_RATES]: {
              clusterCode: resolved.clusterCode,
              specialRateCode: resolved.specialRateCode,
              corporateCode: resolved.corporateCode,
            },
          });
        }
      }
    }
  }, [globalData]);

  const requestId = envVarsObject?.['REQUEST_ID'];
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;
  // Always call the hook - it handles conditional logic internally
  const globalSessionDoc = globalData?.sessionData?.data || globalData?.sessionData?.cacheData?.data;
  useGetUserInfo({
    sessionToken: isConcurFlowActive(featureFlags, globalSessionDoc)
      ? globalData?.sessionData?.sessionToken
      : undefined,
    requestId,
    currentLocale,
  });

  // Update search form state with Concur code when user details are loaded
  useEffect(() => {
    if (!isStoreInitialized) return;
    if (!isConcurFlowActive(featureFlags, globalSessionDoc)) return;
    if (!userDetailsState?.isUserDetailsSaved) return;

    // Only apply Concur code once per session
    if (concurCodeApplied.current) {
      return;
    }

    // Check if user has manually overridden Concur in this session
    if (hasConcurUserOverride()) {
      concurCodeApplied.current = true; // Mark as handled to prevent reprocessing
      return;
    }

    const currentState = useSearchFormStore.getState();
    const concurCode = extractConcurCode(userDetailsState); // Extract on-the-fly

    const resolved = getSpecialRatesForSearch(
      currentState.specialRates?.specialRateCode, // Zustand uses specialRateCode
      currentState.specialRates?.corporateCode,
      concurCode,
      currentState.usePoints?.rewardsRedemption,
      model?.specialRatesListItems || []
    );

    // Update if needed - Zustand store structure
    if (
      currentState.specialRates?.clusterCode !== resolved.clusterCode ||
      currentState.specialRates?.specialRateCode !== resolved.specialRateCode ||
      currentState.specialRates?.corporateCode !== resolved.corporateCode
    ) {
      setSearchFormState([SPECIAL_RATES], {
        [SPECIAL_RATES]: {
          clusterCode: resolved.clusterCode, // Label (e.g., "Corp/Promo Code")
          specialRateCode: resolved.specialRateCode, // Code (e.g., 'corp')
          corporateCode: resolved.corporateCode, // Actual corporate code value
        },
      });
      // Mark that Concur was applied
      concurCodeApplied.current = true;
    }
  }, [userDetailsState?.isUserDetailsSaved, featureFlags, globalData, isStoreInitialized]);

  // Clear override flag on sign-out
  useEffect(() => {
    if (globalData?.sessionData?.authenticated === false) {
      clearConcurUserOverride();
    }
  }, [globalData?.sessionData?.authenticated]);

  // Reset Concur flag when form is submitted or user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      concurCodeApplied.current = false;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Sync store initialization state from TabbedSearchForm
  useEffect(() => {
    if (isShopStoreInitializedFromStore) {
      setIsStoreInitialized(true);
    }
  }, [isShopStoreInitializedFromStore]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.document) {
      setIsClientSideRenderedSearchform(true);
    }

    setFormLoaded(true); // setting this state as true , as search form is loaded on client side for Advanced search page
    const getSession = async () => {
      // Extract the sessionID cookie value from browser cookies
      const sessionID = document?.cookie?.split('sessionID=')[1]?.split(';')[0];
      // Make the client side GET session call
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await getSessionData(sessionID, false, false, requestId);

      // Always initialize store so destination, dates, rooms etc. are populated
      shopStoreInitialization(response?.sessionData, model);
      setIsStoreInitialized(true);

      // Re-apply Concur special rates if they were set before store reinitialization
      const featureFlagsLocal = useClientEnvVarsStore.getState().envVarsObject['featureFlags'] as unknown as
        | Record<string, any>
        | undefined;
      const responseSessionDoc = response?.sessionData?.data || response?.sessionData?.cacheData?.data;
      if (
        isConcurFlowActive(featureFlagsLocal, responseSessionDoc) &&
        concurCodeApplied.current &&
        !hasConcurUserOverride()
      ) {
        const concurCode = extractConcurCode(useUserDetailsGlobalStore.getState());
        if (concurCode) {
          const resolved = getSpecialRatesForSearch(null, null, concurCode, false, model?.specialRatesListItems || []);
          setSearchFormState([SPECIAL_RATES], {
            [SPECIAL_RATES]: {
              clusterCode: resolved.clusterCode,
              specialRateCode: resolved.specialRateCode,
              corporateCode: resolved.corporateCode,
            },
          });
        }
      }

      // Always update globalData so other components get the session
      updateGlobalData('sessionData', response?.sessionData);
    };
    // make the client side session call from SearchForm component
    if (callGetSessionData && typeof window !== 'undefined') {
      const windowWithSessionFetchFlag = window as Window & {
        [SEARCHFORM_SESSION_FETCH_KEY]?: boolean;
      };

      if (!windowWithSessionFetchFlag[SEARCHFORM_SESSION_FETCH_KEY]) {
        windowWithSessionFetchFlag[SEARCHFORM_SESSION_FETCH_KEY] = true;
        // to prevent overwriting Concur values with session data
        const featureFlagsLocal = useClientEnvVarsStore.getState().envVarsObject['featureFlags'] as unknown as
          | Record<string, any>
          | undefined;
        const globalSessionDocLocal = globalData?.sessionData?.data || globalData?.sessionData?.cacheData?.data;
        if (!(isConcurFlowActive(featureFlagsLocal, globalSessionDocLocal) && concurCodeApplied.current)) {
          getSession();
        }
      }
    }
    /**
     * to set the homepage Header height
     */
    //since this element is from AEM script, need to handle it with query selector.
    const htlHeaderComp =
      document.querySelector('.m-header') || (isMBOP ? document.querySelector('[role="banner"]') : null);
    const homePageBannerComp = document.querySelector('.ab');
    if (!homePageBannerComp) return;
    const resizeObserver = new ResizeObserver(() => {
      const htlHeaderHeight = htlHeaderComp?.getBoundingClientRect().height ?? HOME_PAGE_HEADER_CONST_HEIGHT;
      const height = homePageBannerComp ? homePageBannerComp?.clientHeight : 0;
      setHomePageBannerCompHeight(height);
      setTopPosition(htlHeaderHeight + height);
    });
    resizeObserver.observe(homePageBannerComp);
    return () => resizeObserver.disconnect(); // clean up
  }, []);

  // For book page. Because the 800error page of book uses the same component, when the currentLocale is undefined, retrieve the locale from sessionData
  useEffect(() => {
    const sessionData = !!(typeof window !== 'undefined' && window.document) && window['sessionData'];
    currentLocale ?? setCurrentLocale(sessionData?.locale?.replace('_', '-'));
  }, []);

  const scrollPosition = useRef<number>(0);
  const searchFormContainerElRef = useRef<HTMLDivElement>(null);
  const searchFormContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollPosition = useRef<number>(0);
  const scrollThreshold = useRef(0);
  const [searchformScrollThreshold, setSearchformScrollThreshold] = useState(0);
  const [homePageHeaderHeight, setHomePageHeaderHeight] = useState(0);
  // Convert to state to fix React Compiler error - used for calculating top position
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [homePageBannerCompHeight, setHomePageBannerCompHeight] = useState(0);
  const searchFormPadding = 24;
  const [topPosition, setTopPosition] = useState(0);

  const searchFormPositionWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isPositionedExternally) {
      if (refCallback && searchFormContainerRef.current) {
        refCallback(
          searchFormContainerRef.current,
          // this is hacky and should be removed ASAP,
          // but we need to account for the form's initial top-padding
          searchFormContainerRef.current?.getBoundingClientRect().top -
            (searchFormContainerElRef?.current?.getBoundingClientRect().top || 0)
        );
      }
      return;
    }

    /**
     * to set the homepage Header height
     */
    //since this element is from AEM script, need to handle it with query selector.
    const htlHeaderComp =
      document.querySelector('.m-header') || (isMBOP ? document.querySelector('[role="banner"]') : null);
    const homePageBannerComp = document.querySelector('.ab');
    const htlHeaderHeight = htlHeaderComp?.getBoundingClientRect().height ?? HOME_PAGE_HEADER_CONST_HEIGHT;
    const height = homePageBannerComp ? homePageBannerComp?.clientHeight : 0;
    setHomePageBannerCompHeight(height);

    const inititalTopPosition = htlHeaderHeight + height;
    setTopPosition(inititalTopPosition);

    // set initial top position for search form, references htl fragment header
    setSearchformScrollThreshold(htlHeaderHeight + height + searchFormPadding);
    setHomePageHeaderHeight(htlHeaderHeight);
  }, []);

  const setSearchFormTopPosition = () => {
    const offsetVal = isBaseLineSearchTabbedView ? 144 : 24;
    let searchFormTopPositionValue = searchFormPositionWrapperRef?.current?.getBoundingClientRect()?.top;
    if (searchFormTopPositionValue && searchFormTopPositionValue < offsetVal) {
      searchFormTopPositionValue = 0;
    }
    if (searchFormTopPositionValue) {
      setSearchFormState(['searchFormTopPosition'], {
        searchFormTopPosition: {
          value: isBaseLineSearchTabbedView ? searchFormTopPositionValue - offsetVal : searchFormTopPositionValue,
        },
      });
    } else {
      setSearchFormState(['searchFormTopPosition'], {
        searchFormTopPosition: {
          value: window.scrollY,
        },
      });
    }
  };

  /**
   * Below use effect attaches an event for scroll event and is written in such a way that it
   * only triggers the auto scroll functionality.
   *  What is Auto Scroll functionality ? --> When user open DatePicker or any other component(dropdown modal),
   * for some users the screen size is smaller to accomodate the complete modal in the viewport with respect to search form position,
   * hence we auto scroll the searchform to the top so that the dropdown modal will is completely visible to the user.
   */
  useEffect(() => {
    if (!model.isTabbedSearchForm || isBaseLineSearchTabbedView) {
      setSearchFormTopPosition();
    }
  }, [model.isTabbedSearchForm, isBaseLineSearchTabbedView, isClientSideRenderedSearchform]);

  /**
   * checks the scroll position wrt threshold(header) so as to add the necessary scroll CSS
   */
  const handleScrollPosition = (updatedCurrentScrollPosition: number, scrollThreshold: number) => {
    if (updatedCurrentScrollPosition > scrollThreshold && scrollPosition.current < scrollThreshold) {
      scrollPosition.current = updatedCurrentScrollPosition;

      /**
       *makes the SearchForm full width if user scrolls and vice versa if back to original state
       */

      if (searchFormContainerElRef.current) {
        searchFormContainerElRef.current?.classList.add('color-scheme1');
      }
      if (searchFormContainerRef.current) {
        searchFormContainerRef.current?.classList.remove('search-form-wrapper');
      }
    } else if (updatedCurrentScrollPosition <= scrollThreshold && scrollPosition.current > scrollThreshold) {
      scrollPosition.current = updatedCurrentScrollPosition;
      if (searchFormContainerElRef.current) {
        searchFormContainerElRef.current?.classList.remove('color-scheme1');
      }
      if (searchFormContainerRef.current) {
        searchFormContainerRef.current?.classList.add('search-form-wrapper');
      }
    }
  };

  /**
   * function to handle the scroll state of the searchForm when user scrolled(fsd-80422)
   */
  const handleScroll = () => {
    const currentScrollPosition = window.scrollY;
    scrollThreshold.current = searchformScrollThreshold;
    let searchformScrollThresholdVar = searchformScrollThreshold;

    //since this element is from AEM header or alert banner, need to handle it with query selector.
    const alertBanner = document.querySelector('.ab__desc');
    const alertBannerComp = document.querySelector('.ab');
    const alertBannerHeight = alertBannerComp?.clientHeight ?? 0;
    const htlHeaderComp =
      document.querySelector('.m-header') || (isMBOP ? document.querySelector('[role="banner"]') : null);
    const htlHeaderTop = htlHeaderComp?.getBoundingClientRect().top;

    let updatedCurrentScrollPosition = currentScrollPosition;
    if (
      currentScrollPosition > lastScrollPosition.current &&
      currentScrollPosition >= homePageHeaderHeight &&
      htlHeaderTop
    ) {
      updatedCurrentScrollPosition += Math.abs(htlHeaderTop);
      scrollThreshold.current = scrollThreshold.current + Math.abs(htlHeaderTop);
      searchformScrollThresholdVar += Math.abs(htlHeaderTop);
      if (updatedCurrentScrollPosition < (isMBOP ? searchformScrollThresholdVar : scrollThreshold.current)) {
        const calculatedTop = homePageHeaderHeight + alertBannerHeight;
        setTopPosition(calculatedTop);
      }
    }
    /**
     * Close the alert Banner on the scroll of the page
     */
    if (updatedCurrentScrollPosition > alertBannerHeight) {
      alertBanner && alertBanner?.classList.add('hide-visibility');
      alertBannerComp && alertBannerComp?.classList.add('hide-alert');
    }
    /**
     * checks the scroll previous position and current position
     */
    /**
     * remove the sticky css class if searchForm back to top
     */
    if (updatedCurrentScrollPosition === 0) {
      scrollPosition.current = 0;
      searchFormContainerElRef.current?.classList.remove('sticky-search-top');
      searchFormContainerElRef.current?.classList.remove('sticky-search-container');
      searchFormContainerElRef.current?.classList.remove('search-container-top');
      const topPosition = homePageHeaderHeight + alertBannerHeight;

      //when updatedCurrentScrollPosition === 0 , searchform should have 'search-form-wrapper' class applied.
      if (searchFormContainerElRef.current) {
        searchFormContainerElRef.current?.classList.remove('color-scheme1');
      }
      if (searchFormContainerRef.current) {
        searchFormContainerRef.current?.classList.add('search-form-wrapper');
      }
      setTopPosition(topPosition);
    } else if (updatedCurrentScrollPosition > (isMBOP ? searchformScrollThresholdVar : scrollThreshold.current)) {
      // When scroll position is greater than the top offset of the searchform
      /**
       * When scrolling down add the sticky search container class
       */
      if (updatedCurrentScrollPosition > lastScrollPosition.current) {
        handleScrollPosition(
          updatedCurrentScrollPosition,
          isMBOP ? searchformScrollThresholdVar : scrollThreshold.current
        );
        searchFormContainerElRef.current?.classList.add('sticky-search-container');
        searchFormContainerElRef.current?.classList.remove('sticky-search-top');
        searchFormContainerElRef.current?.classList.remove('search-container-top');
      } else {
        /**
         * When scrolling up remove the sticky search container class
         */
        handleScrollPosition(
          updatedCurrentScrollPosition,
          isMBOP ? searchformScrollThresholdVar : scrollThreshold.current
        );
        searchFormContainerElRef.current?.classList.add('sticky-search-top');
        setTopPosition(homePageHeaderHeight);
        searchFormContainerElRef.current?.classList.remove('sticky-search-container');
      }
    } else if (
      updatedCurrentScrollPosition > lastScrollPosition.current ||
      (updatedCurrentScrollPosition < lastScrollPosition.current && updatedCurrentScrollPosition < homePageHeaderHeight)
    ) {
      // When scroll position is less than or equal to the top offset of the searchform
      handleScrollPosition(
        updatedCurrentScrollPosition,
        isMBOP ? searchformScrollThresholdVar : scrollThreshold.current
      );
      /**
       * When scrolling up or scrolling down, remove the sticky search container class
       */
      searchFormContainerElRef.current?.classList.remove('sticky-search-top');
      searchFormContainerElRef.current?.classList.remove('sticky-search-container');
      searchFormContainerElRef.current?.classList.remove('search-container-top');
    }
    lastScrollPosition.current = currentScrollPosition;
  };

  useEffect(() => {
    if (isPositionedExternally) {
      return;
    }

    /**
     * adding the scroll event if handleScroll triggers
     * removes scroll if were back to original state
     */
    if (!TabbedForm) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [TabbedForm, searchformScrollThreshold]);
  const [formLoaded, setFormLoaded] = useState(false);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAvailabilityScenario = props?.hideDestination && props?.hideBrandsField;
    // Retrieve the URL parameters from the window location search string
    const fromDateParam = urlParams?.get('fromDate') || urlParams?.get('fromDates');
    const fromDates = fromDateParam ? getDateFormattedFromURL(fromDateParam) : null;
    const toDateParam = urlParams?.get('toDate') || urlParams?.get('toDates');
    const toDates = toDateParam ? getDateFormattedFromURL(toDateParam) : null;
    const searchCriteria = globalData?.sessionData?.cacheData?.data?.AriesSearch?.searchCriteria;
    // Attempt to retrieve the property code from either The global session data object if available or URL parameters if the global data is not available.
    const propertyCode = urlParams?.get('propertyCode') || searchCriteria?.propertyId;
    const fromDate = fromDates;
    const toDate = toDates;
    const numofRooms = urlParams?.get('numberOfRooms') || searchCriteria?.availabilityRequestVO?.numRooms;
    const numofAdults = urlParams?.get('numberOfGuests') || searchCriteria?.availabilityRequestVO?.numAdultsPerRoom;
    const numofChildren = urlParams?.get('childrenCount') || searchCriteria?.availabilityRequestVO?.numChildrenPerRoom;
    // Check if both 'hideDestination' and 'hideBrandsField' props are true, and if a property code exists.
    if (isAvailabilityScenario && propertyCode) {
      setSearchFormState(
        [DESTINATION],
        {
          [DESTINATION]: {
            displayText: propertyCode,
            destinationAddressPlaceId: propertyCode,
          },
        },
        true
      );
      // Update the search form state to include additional details for the destination field.
      setSearchFormState(['destinationFieldDetails'], {
        destinationFieldDetails: {
          recentlyViewedPopertyCode: propertyCode,
          isRecentSearchSelected: true,
        },
      });
    }
    if (isAvailabilityScenario && fromDate && toDate) {
      const lengthOfStay = searchCriteria?.availabilityRequestVO?.lengthOfStay;
      setSearchFormState([DATES], {
        [DATES]: {
          fromDate: getDateObj(fromDate),
          toDate: getDateObj(toDate),
          flexible: searchCriteria?.availabilityRequestVO?.flexibleDate,
          numberOfNights:
            fromDate && toDate
              ? Number(getTotalNumberOfDays(moment(fromDate), moment(toDate))?.toFixed(0) || 1)
              : lengthOfStay,
          lengthOfStay: fromDate && toDate ? getTotalNumberOfDays(moment(fromDate), moment(toDate)) : lengthOfStay,
        },
      });
    }
    if (isAvailabilityScenario && numofRooms) {
      setSearchFormState([ROOMS_AND_GUESTS], {
        [ROOMS_AND_GUESTS]: {
          numRooms: getRoomRange(numofRooms),
          ...(numofAdults && { numAdultsPerRoom: parseInt(numofAdults, 10) }),
          ...(numofChildren && { numChildrenPerRoom: parseInt(numofChildren, 10) }),
        },
      });
    } else return;
  }, [globalData?.sessionData]);

  const staticDataContextValue = useMemo(() => ({ ...model, currentLocale }), [model, currentLocale]);

  const getSearchformComponent = (TabbedForm: boolean) => {
    if (TabbedForm) {
      return (
        <div ref={isBaseLineSearchTabbedView ? searchFormPositionWrapperRef : undefined}>
          <StyledSearchForm
            data-component-name="o-shop-searchform"
            data-testid="searchform"
            ref={searchFormContainerElRef}
            className={clsx(
              'd-flex',
              model.isTabbedSearchForm !== true && 'p-3 pt-5',
              'document_search_form_container_el_ref'
            )}
          >
            <StaticDataContextProvider value={staticDataContextValue}>
              <div
                ref={searchFormContainerRef}
                className={clsx(
                  'container px-0',
                  model.isTabbedSearchForm ? '' : 'search-form-wrapper',
                  'document_search_form_container',
                  'color-scheme1'
                )}
              >
                {!formLoaded ? (
                  <></>
                ) : (
                  <SearchFormView
                    {...{
                      sessionData: globalData,
                      model,
                      currentLocale,
                      hideBrandsField: props.hideBrandsField,
                      hideDestination: props.hideDestination,
                      isNonStickyVariant: isNonStickyVariant,
                      destinationColStyleOverride: props.destinationColStyleOverride,
                    }}
                  ></SearchFormView>
                )}
              </div>
            </StaticDataContextProvider>
          </StyledSearchForm>
        </div>
      );
    } else {
      const formContent = (
        <StyledSearchForm
          ref={searchFormContainerElRef}
          className={clsx(
            'd-flex',
            model.isTabbedSearchForm !== true && 'p-3 pt-4',
            'document_search_form_container_el_ref',
            'pt-0 pb-0',
            {
              'search-wrapped': isPositionedExternally,
              'sticky-search-wrapped color-scheme1': isPositionedExternally && isSticky,
            }
          )}
          style={{ top: toRem(topPosition) }}
        >
          <StaticDataContextProvider value={staticDataContextValue}>
            <div
              ref={searchFormContainerRef}
              className={clsx(
                'container px-0',
                model.isTabbedSearchForm ? '' : 'search-form-wrapper',
                'document_search_form_container',
                { 'color-scheme1': !isPositionedExternally || !isSticky }
              )}
            >
              <SearchFormView
                {...{
                  sessionData: globalData,
                  model,
                  currentLocale,
                  hideBrandsField: props.hideBrandsField,
                  hideDestination: props.hideDestination,
                  isNonStickyVariant: isNonStickyVariant,
                  destinationColStyleOverride: props.destinationColStyleOverride,
                  isSticky: isSticky,
                  enableConversationalAISearch: enableConversationalAISearch,
                }}
              ></SearchFormView>
            </div>
          </StaticDataContextProvider>
        </StyledSearchForm>
      );
      return (
        <StyledSearchFormWrapper
          data-component-name="o-shop-searchform"
          data-testid="searchform"
          data-test={activityName && experienceName ? 'true' : undefined}
          data-experiment-name={activityName}
          data-variant={experienceName}
          ref={searchFormPositionWrapperRef}
          className={clsx(props?.model?.['styleclass'] ? '' : 'm-container-fullbleed', {
            'search-form-position': searchFormOnHomepage,
          })}
          topOffset={toRem(topPosition)}
          isNonStickyVariant={isNonStickyVariant}
        >
          {formContent}
        </StyledSearchFormWrapper>
      );
    }
  };

  /**
   * TODO: All store calculation will be done as part of this file
   * We will pass proccess data to useSEarchForm in the form of props
   */
  return <>{getSearchformComponent(TabbedForm)}</>;
});

//Config for aem editable components
export const SearchFormConfig = {
  emptyLabel: 'SearchForm',
  isEmpty: false,
  resourceType: `mi-aem-shop-spa/components/content/homepagesearch/v1/homepagesearch`,
};
//Component wrapped with editable component for aem authoring
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SearchFormEditable = (props: any) =>
  props.cqPath.includes('experiencefragment') ? (
    <SearchForm {...props} />
  ) : (
    <EditableComponent config={SearchFormConfig} {...props}>
      <SearchForm {...props} />
    </EditableComponent>
  );
